// uiarc: multi-select (https://uiarc.dev/r/multi-select.json) — Radix/el yapımı menü yerine Base UI Popover,
// CSS Modules yerine Tailwind. Tetikleyicide seçili değerler çip olarak durur (her çip genişliği yayla açılan bir
// yuvadadır, komşular onunla kayar), fazlası "+N" olur ve sayı döner; seçili işaret çizilir. Menüde ok tuşları
// vurguyu gezdirir, Enter/Boşluk işaretler.
// Sapmalar: API proje sözleşmesi (`selected`/`onChange`, `withApply` taslağı); etiket tetikleyicinin üstünde
// (uiarc gibi), seçim yokken "Tümü"; seçenek rolü `option` (liste kutusu), onay kutusu yok.
import { ChevronDown, X } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'motion/react'
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface MultiSelectProps {
  label: string
  options: MultiSelectOption[]
  selected: string[]
  onChange: (values: string[]) => void
  /**
   * Rapor filtreleri: seçimler taslakta birikir, "Uygula" ile tek `onChange` çağrılır
   * (tarih aralığı seçicisiyle aynı). "Vazgeç", Escape ya da dışarı tıklamak taslağı atar.
   */
  withApply?: boolean
  /** Görünmez etiket: üstteki etiketi çağıran verir (`Field` içinde). Erişilebilir ad `label`dır. */
  hideLabel?: boolean
  placeholder?: string
  /** Tetikleyicide çip olarak gösterilen seçim sayısı; kalanı "+N". */
  maxVisible?: number
  className?: string
}

const { blur, duration, ease, spring } = motionTokens
/** Her çip genişliği yayla açılıp kapanan bir yuvadadır; komşular onunla gider, hiçbir şey üst üste binmez. */
const slot: Variants = {
  hidden: { width: 0, opacity: 0 },
  shown: {
    width: 'auto',
    opacity: 1,
    transition: {
      width: spring.smooth,
      opacity: { duration: duration.fast, ease: ease.enter },
    },
  },
  gone: {
    width: 0,
    opacity: 0,
    transition: {
      width: spring.smooth,
      opacity: { duration: duration.instant, ease: ease.standard },
    },
  },
}
/** Çipin kendisi .9'dan yumuşak bulanıklıkla büyür, çıkarken küçülür. */
const chip: Variants = {
  hidden: { scale: 0.9, filter: `blur(${blur.soft}px)` },
  shown: {
    scale: 1,
    filter: 'blur(0px)',
    transition: { ...spring.snappy, filter: { duration: duration.standard, ease: ease.enter } },
  },
  gone: {
    scale: 0.9,
    filter: `blur(${blur.subtle}px)`,
    transition: { duration: duration.instant, ease: ease.standard },
  },
}
/** Azaltılmış harekette kısa solmalar; dinlenme durumları hareketli varyantlarla aynıdır. */
const fade: Variants = {
  hidden: { opacity: 0 },
  shown: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: duration.instant } },
  gone: { opacity: 0, transition: { duration: duration.instant } },
}
const slotFade: Variants = {
  hidden: { opacity: 0 },
  shown: { width: 'auto', opacity: 1, transition: { duration: duration.instant } },
  gone: { opacity: 0, transition: { duration: duration.instant } },
}
const chipStill: Variants = {
  hidden: { scale: 1, filter: 'blur(0px)' },
  shown: { scale: 1, filter: 'blur(0px)' },
  gone: { scale: 1, filter: 'blur(0px)' },
}
/** Taşan sayı döner: büyüyen sayı aşağıdan yükselir, küçülen yukarıdan iner. */
const roll: Variants = {
  hidden: (direction: number) => ({
    opacity: 0,
    y: `${direction * 0.5}em`,
    filter: `blur(${blur.subtle}px)`,
  }),
  shown: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: duration.standard, ease: ease.enter },
  },
  gone: (direction: number) => ({
    opacity: 0,
    y: `${direction * -0.5}em`,
    filter: `blur(${blur.subtle}px)`,
    transition: { duration: duration.instant, ease: ease.standard },
  }),
}

/** Seçilince kendini çizen, kaldırılınca geri çekilen işaret. */
function CheckMark({ reduce }: { reduce: boolean | null }) {
  return (
    <svg
      className="flex-none"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <motion.path
        d="M4 12.5 9 17.5 20 6.5"
        initial={reduce ? { opacity: 0 } : { pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        exit={reduce ? { opacity: 0 } : { pathLength: 0, opacity: 0 }}
        transition={
          reduce
            ? { duration: duration.instant }
            : {
                pathLength: { duration: duration.standard, ease: ease.enter },
                opacity: { duration: duration.instant },
              }
        }
      />
    </svg>
  )
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((item) => b.includes(item))
}

/** Tetikleyici + çipler + işaretli seçenek listesi. Seçenek/etiket anlamı çağırandan gelir. */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  withApply = false,
  hideLabel = false,
  placeholder = messages.common.all,
  maxVisible = 2,
  className,
}: MultiSelectProps) {
  const id = useId()
  const labelId = `${id}-label`
  const valueId = `${id}-value`
  const reduce = useReducedMotion()
  const listRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(selected)
  const [activeIndex, setActiveIndex] = useState(-1)
  // Taslak yalnız `withApply`da; her açılışta uygulanmış seçimden kurulur.
  const current = withApply && open ? draft : selected
  const currentSet = useMemo(() => new Set(current), [current])
  const labelFor = (value: string) =>
    options.find((option) => option.value === value)?.label ?? value
  const visible = selected.slice(0, maxVisible).map((value) => ({ value, label: labelFor(value) }))
  const remaining = Math.max(0, selected.length - visible.length)
  const [previousRemaining, setPreviousRemaining] = useState(remaining)
  const [countDirection, setCountDirection] = useState(1)
  if (previousRemaining !== remaining) {
    setPreviousRemaining(remaining)
    setCountDirection(remaining > previousRemaining ? 1 : -1)
  }

  const change = (next: string[]) => {
    if (withApply) setDraft(next)
    else onChange(next)
  }
  const toggle = (option: MultiSelectOption) => {
    if (option.disabled) return
    change(
      currentSet.has(option.value)
        ? current.filter((item) => item !== option.value)
        : [...current, option.value],
    )
  }
  const openChange = (next: boolean) => {
    setOpen(next)
    setActiveIndex(-1)
    if (next) setDraft(selected)
  }
  const apply = () => {
    if (!sameSet(draft, selected)) onChange(draft)
    setOpen(false)
  }
  const clearSelected = () => {
    onChange([])
    setDraft([])
    setOpen(false)
  }

  const enabled = options.flatMap((option, index) => (option.disabled ? [] : [index]))
  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      const option = options[activeIndex]
      if (option) {
        event.preventDefault()
        toggle(option)
      }
      return
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && enabled.length) {
      event.preventDefault()
      const at = enabled.indexOf(activeIndex)
      const down = event.key === 'ArrowDown'
      const next =
        at < 0
          ? down
            ? 0
            : enabled.length - 1
          : down
            ? (at + 1) % enabled.length
            : (at - 1 + enabled.length) % enabled.length
      const index = enabled[next]!
      setActiveIndex(index)
      listRef.current
        ?.querySelector<HTMLElement>(`[data-index="${index}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    }
  }

  const summary = selected.length ? selected.map(labelFor).join(', ') : placeholder

  return (
    <div className={cn('grid w-56 min-w-0 gap-[7px]', className)}>
      <Label id={labelId} className={hideLabel ? 'sr-only' : undefined}>
        {label}
      </Label>
      <div className="relative min-w-0">
        <Popover open={open} onOpenChange={openChange}>
          <PopoverTrigger
            data-slot="multi-select-trigger"
            className={cn(
              'group/trigger border-border bg-surface text-foreground rounded-control relative flex min-h-(--control-height-md) w-full min-w-0 cursor-pointer items-center gap-2 border py-[5px] pr-[11px] pl-3 text-left text-sm',
              'ease-standard [transition:border-color_var(--duration-fast)_var(--ease-standard),background-color_var(--duration-fast)_var(--ease-standard)] motion-reduce:transition-none',
              'hover:bg-surface-muted hover:border-border-strong aria-expanded:border-border-strong aria-expanded:bg-surface-muted active:border-border-strong active:bg-surface-muted',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            aria-labelledby={`${labelId} ${valueId}`}
            onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
              if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !open) {
                event.preventDefault()
                openChange(true)
              }
            }}
          >
            <span id={valueId} className="sr-only">
              {summary}
            </span>
            <span
              className="relative flex min-h-6.5 min-w-0 flex-1 items-center overflow-hidden"
              aria-hidden="true"
            >
              <AnimatePresence initial={false}>
                {visible.map((item) => (
                  <motion.span
                    key={`chip-${item.value}`}
                    className="flex min-w-0 flex-[0_1_auto] overflow-hidden"
                    variants={reduce ? slotFade : slot}
                    initial="hidden"
                    animate="shown"
                    exit="gone"
                  >
                    <motion.span
                      className="bg-surface-muted text-foreground rounded-pill group-aria-expanded/trigger:bg-surface group-hover/trigger:bg-surface leading-body mr-[5px] max-w-36 flex-none overflow-hidden px-2 py-1 text-xs text-ellipsis whitespace-nowrap shadow-[inset_0_0_0_1px_var(--border-subtle)] [transition:background-color_var(--duration-fast)_var(--ease-standard)] motion-reduce:transition-none"
                      variants={reduce ? chipStill : chip}
                    >
                      {item.label}
                    </motion.span>
                  </motion.span>
                ))}
                {remaining > 0 && (
                  <motion.span
                    key="more"
                    className="flex min-w-0 flex-shrink-0 overflow-hidden"
                    variants={reduce ? slotFade : slot}
                    initial="hidden"
                    animate="shown"
                    exit="gone"
                  >
                    <motion.span
                      className="bg-surface-muted text-text-secondary rounded-pill group-aria-expanded/trigger:bg-surface group-hover/trigger:bg-surface leading-body mr-[5px] inline-flex flex-none px-2 py-1 text-xs tabular-nums shadow-[inset_0_0_0_1px_var(--border-subtle)] [transition:background-color_var(--duration-fast)_var(--ease-standard)] motion-reduce:transition-none"
                      variants={reduce ? chipStill : chip}
                    >
                      +
                      <span className="inline-grid *:col-start-1 *:row-start-1">
                        <AnimatePresence initial={false} custom={countDirection}>
                          <motion.span
                            key={remaining}
                            custom={countDirection}
                            variants={reduce ? fade : roll}
                            initial="hidden"
                            animate="shown"
                            exit="gone"
                          >
                            {remaining}
                          </motion.span>
                        </AnimatePresence>
                      </span>
                    </motion.span>
                  </motion.span>
                )}
                {!selected.length && (
                  <motion.span
                    key="placeholder"
                    className="text-text-muted absolute inset-0 flex items-center overflow-hidden whitespace-nowrap"
                    variants={fade}
                    initial="hidden"
                    animate="shown"
                    exit="gone"
                  >
                    {placeholder}
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            <ChevronDown
              className={cn(
                'text-text-muted size-4 flex-none [transition:transform_var(--duration-spring)_var(--ease-spring)] group-aria-expanded/trigger:rotate-180 motion-reduce:transition-none',
                // Seçimde temizle düğmesi için yer ayrılır; ok sağ kenara sabit kalır.
                selected.length > 0 && 'ml-6.5',
              )}
              aria-hidden="true"
            />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={8}
            initialFocus={listRef}
            // Uygula satırı (Temizle/Vazgeç/Uygula) dar tetikleyiciden taşmasın diye en az 16rem.
            className="w-[max(var(--anchor-width),16rem)] max-w-[calc(100vw-20px)] min-w-(--anchor-width) gap-0 p-[5px]"
          >
            <div
              ref={listRef}
              id={`${id}-listbox`}
              role="listbox"
              aria-label={label}
              aria-multiselectable="true"
              aria-activedescendant={
                activeIndex >= 0 ? `${id}-option-${options[activeIndex]?.value}` : undefined
              }
              tabIndex={0}
              className="max-h-64 overflow-y-auto overscroll-contain outline-none"
              onKeyDown={onListKeyDown}
            >
              {options.length === 0 ? (
                <p className="text-text-muted p-3 text-sm">{messages.filters.noOption}</p>
              ) : (
                options.map((option, index) => (
                  <button
                    key={option.value}
                    id={`${id}-option-${option.value}`}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    data-index={index}
                    data-active={activeIndex === index || undefined}
                    aria-selected={currentSet.has(option.value)}
                    aria-disabled={option.disabled || undefined}
                    disabled={option.disabled}
                    className={cn(
                      'text-foreground flex min-h-9.5 w-full cursor-pointer items-center justify-between gap-3 rounded-[calc(var(--radius-panel)-5px)] border-0 bg-transparent px-[11px] text-left text-sm',
                      '[transition:background-color_80ms_var(--ease-standard),color_80ms_var(--ease-standard)] motion-reduce:transition-none',
                      'hover:bg-surface-muted data-[active]:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40',
                    )}
                    onPointerMove={() => {
                      if (activeIndex !== index && !option.disabled) setActiveIndex(index)
                    }}
                    onClick={() => toggle(option)}
                  >
                    <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {option.label}
                    </span>
                    <AnimatePresence initial={false}>
                      {currentSet.has(option.value) && <CheckMark key="check" reduce={reduce} />}
                    </AnimatePresence>
                  </button>
                ))
              )}
            </div>
            {withApply ? (
              <div className="border-border-subtle mt-1 flex items-center gap-1 border-t px-1 pt-2 pb-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="mr-auto"
                  disabled={draft.length === 0}
                  onClick={() => setDraft([])}
                >
                  {messages.common.clear}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                  {messages.common.cancel}
                </Button>
                <Button size="sm" onClick={apply}>
                  {messages.filters.apply}
                </Button>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
        <AnimatePresence initial={false}>
          {selected.length > 0 && (
            <motion.button
              type="button"
              data-slot="multi-select-clear"
              aria-label={messages.filters.clearSelection}
              className="text-text-muted rounded-pill hover:bg-surface hover:text-foreground absolute inset-y-0 right-9 my-auto grid size-5.5 cursor-pointer place-items-center border-0 bg-transparent [transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard)] after:absolute after:-inset-[5px] after:content-[''] motion-reduce:transition-none"
              onClick={clearSelected}
              initial={
                reduce
                  ? { opacity: 0 }
                  : { opacity: 0, scale: 0.6, filter: `blur(${blur.subtle}px)` }
              }
              animate={{
                opacity: 1,
                scale: 1,
                filter: 'blur(0px)',
                transition: reduce
                  ? { duration: duration.instant }
                  : { ...spring.snappy, opacity: { duration: duration.fast } },
              }}
              exit={{
                opacity: 0,
                ...(reduce ? {} : { scale: 0.6, filter: `blur(${blur.subtle}px)` }),
                transition: { duration: duration.instant, ease: ease.standard },
              }}
              whileTap={{
                scale: reduce ? 1 : 0.96,
                transition: { duration: duration.instant, ease: ease.standard },
              }}
            >
              <X className="size-3.5" aria-hidden="true" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
